"use client";

import { z } from "zod";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { useTranslations } from "next-intl";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { contactUs } from "@/service/contact-service";
import { useToast } from "@/hooks/use-toast";
import { useRouter } from "@/i18n/routing";

const formSchema = z.object({
  email: z
    .string()
    .email("Please enter a valid email address")
    .max(255, "Email is too long"),
  subject: z
    .string({
      required_error: "Please provide a subject",
    })
    .min(3, "Subject must be at least 3 characters")
    .max(255, "Subject is too long"),
  description: z
    .string({
      required_error: "Please provide a message",
    })
    .min(10, "Message must be at least 10 characters")
    .max(5000, "Message is too long"),
});

export default function ContactUsForm() {
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  const t = useTranslations();
  const router = useRouter();

  const { mutate: contact, isPending } = useMutation({
    mutationFn: (values: z.infer<typeof formSchema>) => contactUs(values),
    onSuccess: () => {
      console.log("Contact form submitted successfully");
      router.push("/contact-us/thank-you");
      toast({
        title: "Message sent successfully",
        variant: "custom",
      });
    },
    onError: (error: any) => {
      console.error(error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to send message. Please try again.";
      
      // Handle rate limiting errors
      if (error?.response?.status === 429) {
        toast({
          title: "Too many requests",
          description: "Please wait a moment before submitting again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
    contact(values);
  }

  return (
    <Form {...form}>
      <form
        className="w-full space-y-5 text-primary-foreground"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="grid gap-2 mb-4">
          <FormField
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder={t("general.email_place_holder")}
                    className="rounded-full bg-black font-poppins placeholder:text-[#AAAAAA] border-[#636363] pl-8 py-6 ring-offset-black-bg"
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-2 mb-4">
          <FormField
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    placeholder={t("general.subject")}
                    className="rounded-full bg-black font-poppins placeholder:text-[#AAAAAA] border-[#636363] pl-8 py-6 ring-offset-black-bg"
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-2 mb-4">
          <FormField
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder={t("general.message")}
                    className="resize-none rounded-3xl bg-black font-poppins placeholder:text-[#AAAAAA] border-[#636363] pl-8 py-6 ring-offset-black-bg"
                  />
                </FormControl>
                <FormMessage className="text-red-400" />
              </FormItem>
            )}
          />
        </div>
        <Button
          type="submit"
          className="w-fit bg-gradient-to-t from-primary to-second-primary rounded-full text-lg md:text-3xl py-3 h-fit px-14 text-primary-foreground hover:text-primary-foreground/80 transition md:translate-y-6"
        >
          {t("general.send_message")}
        </Button>
      </form>
    </Form>
  );
}
